import React, { useCallback, useEffect, useState } from "react";
import Hoc from "../../component/HOC";
import { connect } from "react-redux";
import {
  createProgram,
  getProgramDetails,
  showDetail,
  updateProgram,
  uploadFile,
  getMembershipFeatureCatalogue,
  getMembershipFeatures,
  updateMembershipFeatures,
} from "../../redux/action/user";
import {
  Button,
  Grid,
  Tab,
  Tabs,
  Box,
  InputLabel,
  RadioGroup,
  IconButton,
  FormHelperText,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { Quill } from "react-quill";
import ImageUploader from "quill-image-uploader";
import "./Program.scss";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";
import RichTextEditor from "./TextEditor";
import FormInput from "./FormInput";
import { useNavigate, useParams } from "react-router";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { capitalizeFirstLetter, decodeHtmlEntities } from "../../common";
import { toast } from "react-toastify";
import { WORDPRESS_CATEGORY_API } from "../../constants/config";
import SingleSelect from "../../atoms/SingleSelect";

Quill.register("modules/imageUploader", ImageUploader);
Quill.register("modules/imageResize", ImageResize);


const validateField = (field, value, formData) => {
  let errorText = "";
  switch (field) {
    case "name":
      if (!value.trim()) errorText = "Name is required";
      break;

    case "amount":
      if (!value) {
        errorText = "Amount is required";
      } else if (isNaN(value)) {
        errorText = "Amount must be a number";
      } else if (Number(value) < 0) {
        errorText = "Amount cannot be negative";
      } else if (Number(value) > 50000) {
        errorText = "Amount cannot exceed 50000";
      }
      break;

    case "currency":
      if (!value) errorText = "Currency is required";
      break;

    case "description":
      if (!value.trim()) errorText = "Description is required";
      break;

    case "wordpressCategoryId":
      if (Array.isArray(value) ? value.length === 0 : !value) {
        errorText = "Category is required";
      }
      break;

    case "unit":
      if (!value) errorText = "Unit is required";
      break;

    case "image":
      if (!value) errorText = "Image is required";
      break;


    default:
      break;
  }

  return errorText ? { error: true, errorText } : { error: false, errorText: "" };
};


const AddProgram = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isCorporate, setIsCorporate] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    amount: '',
    currency: "usd",
    // duration: 0,
    unit: "Lifetime",
    description: "",
    wordpressCategoryId: [],
    wordpressCategoryName: [],
    programType: "program",
    image: null,
    programUrl: "",
    showOnVitalityPortal: true,
    availableToFreePlan: false,
  });

  // Rich Text Editors state
  const [editorContents, setEditorContents] = useState([""]);
  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState({
    name: { error: false, errorText: "" },
    amount: { error: false, errorText: "" },
    currency: { error: false, errorText: "" },
    description: { error: false, errorText: "" },
    wordpressCategoryId: { error: false, errorText: "" },
    // duration: { error: false, errorText: "" },
    unit: { error: false, errorText: "" },
    image: { error: false, errorText: "" },
    // programUrl: { error: false, errorText: "" },
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [savedCategoryNames, setSavedCategoryNames] = useState([]);
  const [displayCategoryNames, setDisplayCategoryNames] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categorySavedRef = React.useRef(false);
  const [membershipFeatures, setMembershipFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [featuresMessage, setFeaturesMessage] = useState("");
  const isNewProgram = id === "new" || !id;
  // Free Plan access only applies to a $0 program, never a paid one or a subscription.
  const isFreeProgram =
    formData.programType === "program" &&
    !isCorporate &&
    Number(formData.amount) === 0 &&
    String(formData.amount).trim() !== "";

  const buildDefaultFeaturesFromCatalogue = (catalogue, programName = "") => {
    const isExplorer = String(programName || "")
      .toLowerCase()
      .includes("explorer");
    const list = Array.isArray(catalogue) ? catalogue : [];
    return list.map((f) => {
      const key = f.key;
      const enabledByDefault = !(
        isExplorer &&
        (key === "order_labs" || key === "book_consult")
      );
      return {
        key,
        name: f.name,
        description: f.description,
        enabled:
          typeof f.enabled === "boolean" ? f.enabled : enabledByDefault,
      };
    });
  };

  const [fullHtmlContent, setFullHtmlContent] = useState(
    generateHtml(formData, editorContents, imagePreview)
  );

  useEffect(() => {
    props.showDetail(true);
    if (isNewProgram) {
      setImagePreview(null);
      setFeaturesLoading(true);
      setFeaturesMessage("");
      props
        .getMembershipFeatureCatalogue()
        .then((catalogue) => {
          setMembershipFeatures(
            buildDefaultFeaturesFromCatalogue(catalogue, formData.name)
          );
        })
        .catch((err) => {
          console.error("Unable to load membership feature catalogue", err);
          setFeaturesMessage(
            err?.response?.data?.message ||
              "Unable to load membership features."
          );
        })
        .finally(() => setFeaturesLoading(false));
    } else if (id) {
      props.getProgramDetails(id);
      setFeaturesLoading(true);
      setFeaturesMessage("");
      props
        .getMembershipFeatures(id)
        .then((data) => {
          setMembershipFeatures(
            Array.isArray(data?.features) ? data.features : []
          );
        })
        .catch((err) => {
          console.error("Unable to load membership features", err);
          setFeaturesMessage(
            err?.response?.data?.message ||
              "Unable to load membership features."
          );
        })
        .finally(() => setFeaturesLoading(false));
    }
    return () => props.showDetail(false);
  }, []);

  // Creating: if name becomes Explorer, default Order Labs / Book a Consult OFF.
  useEffect(() => {
    if (!isNewProgram || membershipFeatures.length === 0) return;
    const isExplorer = String(formData.name || "")
      .toLowerCase()
      .includes("explorer");
    if (!isExplorer) return;
    setMembershipFeatures((prev) =>
      prev.map((f) =>
        f.key === "order_labs" || f.key === "book_consult"
          ? { ...f, enabled: false }
          : f
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.name]);

  useEffect(() => {
    axios
      .get(WORDPRESS_CATEGORY_API)
      .then((res) => {
        setCategories(res?.data || []);
      })
      .catch((err) => {
        console.error("Unable to load WordPress categories", err);
      });
  }, []);


  useEffect(() => {
    if (props.programDetails && props.programDetails?.ID) {
      const data = props.programDetails;
      const showOnVitalityPortal =
        data?.showOnVitalityPortal ??
        data?.show_on_vitality_portal ??
        data?.showOnPortal ??
        data?.show_on_portal ??
        data?.isVisibleOnVitalityPortal ??
        data?.is_visible_on_vitality_portal ??
        true;

      setFormData({
        name: data.name || "",
        amount: data.amount || "",
        currency: data.currency || "usd",
        // duration: data.duration || 0,
        unit: data.interval || "Monthly",
        description: data.description || "",
        wordpressCategoryId: Array.isArray(data.wordpressCategoryId)
          ? data.wordpressCategoryId.map((id) => String(id))
          : String(data.wordpressCategoryId || data.categoryId || data.category || "").split(",").filter(Boolean),
        wordpressCategoryName: Array.isArray(data.wordpressCategoryName)
          ? data.wordpressCategoryName
          : String(data.wordpressCategoryName || data.categoryName || data.category || "").split(",").filter(Boolean),
        programType: data.isSubscription ? "subscription" : "program",
        image: { filePath: data.imageUrl },
        programUrl: data?.url || "",
        showOnVitalityPortal:
          String(showOnVitalityPortal) === "1" || showOnVitalityPortal === true,
        availableToFreePlan:
          String(data?.availableToFreePlan) === "1" ||
          data?.availableToFreePlan === true,
      });
      setIsCorporate(data.isCorporate || false);
      const loadedNames = Array.isArray(data.wordpressCategoryName)
        ? data.wordpressCategoryName
        : String(data.wordpressCategoryName || data.categoryName || data.category || "").split(",").filter(Boolean);
      setSavedCategoryNames(loadedNames);
      setDisplayCategoryNames(loadedNames);
      setImagePreview(data.imageUrl);
      setFullHtmlContent(decodeHtmlEntities(data.summary));

      if (data.includes) {
        const decodedArray = decodeHtmlEntity(data.includes);
        setEditorContents(decodedArray || [""]); // Ensure there's a fallback if no contents
      } else {
        setEditorContents([""]); // If no includes field, set to an empty array
      }
    }
  }, [props.programDetails, id]);

  useEffect(() => {
    if (categories.length > 0 && formData.wordpressCategoryId.length > 0) {
      const resolvedNames = formData.wordpressCategoryId
        .map((id) => {
          const cat = categories.find((item) => String(item.id) === String(id));
          return cat ? cat.name : null;
        })
        .filter(Boolean);

      if (resolvedNames.length > 0) {
        setSavedCategoryNames(resolvedNames);
        setDisplayCategoryNames(resolvedNames);
        setFormData((prev) => ({ ...prev, wordpressCategoryName: resolvedNames }));
      }
    }
  }, [categories]);

  useEffect(() => {
    setFullHtmlContent(generateHtml(formData, editorContents, imagePreview));
  }, [formData, editorContents, imagePreview]); // Depend on state changes

  const decodeHtmlEntity = (encodedArray) => {
    if (encodedArray) {
      return (
        encodedArray &&
        encodedArray?.map((encodedString) => {
          const parser = new DOMParser();
          const decodedString = parser.parseFromString(
            encodedString,
            "text/html"
          ).body.textContent;
          return decodedString;
        })
      );
    } else return [];
  };

  useEffect(() => {
    if (id == "new") {
      setFormData({
        name: "",
        amount: "",
        currency: "usd",
        // duration: 0,
        unit: "Lifetime",
        description: "",
        wordpressCategoryId: [],
        wordpressCategoryName: [],
        programType: "program",
        image: null,
        programUrl: "",
        showOnVitalityPortal: true,
        availableToFreePlan: false,
      });
      setIsCorporate(false);
      setEditorContents([""]);
      setImagePreview(null);
      setDisplayCategoryNames([]);
    }
  }, [id]);

  // Handle file change (image upload)
  const handleSaveCategories = () => {
    if (!formData.wordpressCategoryId || formData.wordpressCategoryId.length === 0) {
      setErrors((prev) => ({
        ...prev,
        wordpressCategoryId: { error: true, errorText: "Category is required" },
      }));
      return;
    }

    const namesFromCategories = formData.wordpressCategoryId
      .map((id) => {
        const cat = categories.find((item) => String(item.id) === String(id));
        return cat ? cat.name : null;
      })
      .filter(Boolean);

    const resolvedNames =
      namesFromCategories.length > 0
        ? namesFromCategories
        : Array.isArray(formData.wordpressCategoryName) && formData.wordpressCategoryName.length > 0
        ? formData.wordpressCategoryName
        : formData.wordpressCategoryId;

    setSavedCategoryNames(resolvedNames);
    setDisplayCategoryNames(resolvedNames);
    categorySavedRef.current = true;
    setCategoryOpen(false);
    setErrors((prev) => ({
      ...prev,
      wordpressCategoryId: { error: false, errorText: "" },
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData1 = new FormData();
      formData1.append("file", file);
      const data = await props.uploadFile(formData1);

      if (data && data.status === 200) {
        setFormData((prevData) => ({
          ...prevData,
          image: data.data,
        }));

        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
      setErrors((prevErrors) => ({ ...prevErrors, image: false }));
    }
  };

  const handleMembershipFeatureToggle = (featureKey) => (e) => {
    const enabled = e.target.checked;
    setMembershipFeatures((prev) =>
      prev.map((f) =>
        f.key === featureKey ? { ...f, enabled } : f
      )
    );
    setFeaturesMessage("");
  };

  const persistMembershipFeatures = async (programId) => {
    if (!programId || membershipFeatures.length === 0) return null;
    const payload = membershipFeatures.map((f) => ({
      key: f.key,
      enabled: Boolean(f.enabled),
    }));
    return props.updateMembershipFeatures(programId, payload);
  };

  const handleSaveMembershipFeatures = async () => {
    if (isNewProgram) {
      setFeaturesMessage(
        "Save the program first — menu options are saved with the program."
      );
      return;
    }
    setFeaturesSaving(true);
    setFeaturesMessage("");
    try {
      const data = await persistMembershipFeatures(id);
      setMembershipFeatures(
        Array.isArray(data?.features) ? data.features : membershipFeatures
      );
      setFeaturesMessage("Membership features saved.");
    } catch (err) {
      console.error("Unable to save membership features", err);
      setFeaturesMessage(
        err?.response?.data?.message ||
          "Unable to save membership features."
      );
    } finally {
      setFeaturesSaving(false);
    }
  };

  const handleFormChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;

      // Restrict to two decimal places for "amount" field
      if (field === "amount") {
        // Allow empty value for clearing the input
        if (value === "") {
          // continue to update
        } else if (!/^\d+(\.\d{0,2})?$/.test(value)) {
          return; // Don't update state if invalid
        }
      }

      setFormData((prevData) => {
        const updatedData = {
          ...prevData,
          [field]: value,
          ...(field === "programType" && {
            // duration: 0,
            unit: value === "program" ? "Lifetime" : "Monthly",
          }),
        };

        // Validate using the updated data
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: validateField(field, value, updatedData),
        }));

        return updatedData;
      });
    },
    []
  );



  // Handle delete for editor content
  const handleDelete = (index) => {
    const updatedContents = [...editorContents];
    updatedContents.splice(index, 1);
    setEditorContents(updatedContents);
    const updatedHtml = generateHtml(formData, updatedContents, imagePreview);
    setFullHtmlContent(updatedHtml);
  };

  // Handle change in text editor
  const handleEditorChange = useCallback(
    (index, value) => {
      setEditorContents((prevContents) => {
        const newContents = [...prevContents];
        newContents[index] = value;
        return newContents;
      });
    },
    []
  );

  // Add a new editor
  const addEditor = () => {
    setEditorContents((prevContents) => [...prevContents, ""]);
  };

  // Handle the change in tabs
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    let newErrors = {};
    let isValid = true;

    // Validate each field
    Object.keys(errors).forEach((field) => {
      const validation = validateField(field, formData[field] || "", formData);
      newErrors[field] = validation;
      if (validation.error) isValid = false;
    });

    // Update errors state
    setErrors(newErrors);

    // Stop submission if validation fails
    if (!isValid) {
      return; // Simply return without submitting
    }

    const formData1 = new URLSearchParams();
    // Append basic fields (use empty string or placeholder values for optional fields if needed)
    formData1.append("name", formData.name || "");
    formData1.append("description", formData.description || "");
    formData1.append("amount", formData.amount || "");
    formData1.append("interval", formData.unit ? formData.unit : "Monthly");
    formData1.append("currency", formData.currency || "");
    formData1.append(
      "wordpressCategoryId",
      Array.isArray(formData.wordpressCategoryId)
        ? formData.wordpressCategoryId.join(",")
        : formData.wordpressCategoryId || ""
    );
    formData1.append(
      "wordpressCategoryName",
      Array.isArray(formData.wordpressCategoryName)
        ? formData.wordpressCategoryName.join(",")
        : formData.wordpressCategoryName || ""
    );
    // formData1.append("duration", formData.duration || 0);
    formData1.append("url", formData.programUrl || null);
    formData1.append("isCorporate", isCorporate ? true : false);
    formData1.append(
      "showOnVitalityPortal",
      formData.showOnVitalityPortal ? "1" : "0"
    );
    formData1.append(
      "availableToFreePlan",
      isFreeProgram && formData.availableToFreePlan ? "1" : "0"
    );
    // Append the HTML content or any structured data
    formData1.append("summary", fullHtmlContent || "");

    if (editorContents && editorContents.length) {
      // Join the contents into a single string using a separator
      const contentString = editorContents.join("|"); // You can replace '||' with any other separator
      formData1.append("includes", contentString);
    } else {
      formData1.append("includes", "");
    }

    // if (formData.image && formData.image.filePath) {
    //   formData1.append("imageUrl", formData.image.filePath);
    // }

    if (formData.image && formData.image.filePath) {
      const filePath = formData.image.filePath.replace(/.*\/uploads\//, "uploads/");
      formData1.append("imageUrl", filePath);
    }

    // Use appropriate method to make the request
    if (isNewProgram) {
      formData1.append("status", "1");
      formData1.append(
        "isSubscription",
        formData.programType === "program" ? false : true
      );
      try {
        const res = await props.createProgram(formData1);
        if (!res || res.status !== 200) {
          toast.error(
            res?.response?.data?.message ||
              res?.data?.message ||
              "Something went wrong"
          );
          return;
        }
        const createdId =
          res?.data?.data?.ID ??
          res?.data?.data?.id ??
          res?.data?.ID ??
          res?.data?.id;
        if (createdId && membershipFeatures.length > 0) {
          try {
            await persistMembershipFeatures(createdId);
          } catch (featureErr) {
            console.error(
              "Program created but membership features failed",
              featureErr
            );
            toast.error(
              "Program saved, but menu feature settings could not be saved. Edit the program to set them."
            );
            return;
          }
        }
        toast.success(res?.data?.message || "Saved successfully");
        navigate("/programs");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Something went wrong"
        );
      }
    } else {
      formData1.append("ID", id);
      try {
        const res = await props.updateProgram(formData1);
        if (!res || res.status !== 200) {
          toast.error(
            res?.response?.data?.message ||
              res?.data?.message ||
              "Something went wrong"
          );
          return;
        }
        if (membershipFeatures.length > 0) {
          try {
            await persistMembershipFeatures(id);
          } catch (featureErr) {
            console.error(
              "Program updated but membership features failed",
              featureErr
            );
            toast.error(
              "Program updated, but menu feature settings could not be saved."
            );
            return;
          }
        }
        toast.success(res?.data?.message || "Updated successfully");
        navigate("/programs");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Something went wrong"
        );
      }
    }
  };

  // Cancel action
  const handleCancel = () => {
    setFormData({
      name: "",
      amount: "",
      currency: "",
      description: "",
      wordpressCategoryId: [],
      wordpressCategoryName: [],
      // duration: 0,
      unit: "Lifetime",
      programType: "program",
      image: null,
      programUrl: "",
      showOnVitalityPortal: true,
      availableToFreePlan: false,
    });
    setSavedCategoryNames([]);
    setImagePreview("");
    setEditorContents([""]);
    navigate(-1);
  };

  return (
    <Box sx={{ ...AddProgramWrapper }}>
      <Hoc subTitle={"Program & Subscriptions"}>
        <Box className="container-fluid p-0 mt-4" sx={{ ...PrgmSubs, mb: 3 }}>
          <Box sx={{ width: "100%", px: 3, ...EditorWrapper }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              aria-label="basic tabs example"
            >
              <Tab label="Editor" />
              <Tab label="Preview" />
            </Tabs>
            <div style={{ display: activeTab === 0 ? "block" : "none" }}>
              <Grid container spacing={2} className="FieldsWrap">
                <Grid item xs={12} md={12}>
                  <FormInput
                    label="Name"
                    value={formData.name}
                    onChange={handleFormChange("name")}
                    placeholder="Enter Program Name"
                    error={errors?.name?.error}
                    helperText={errors?.name?.errorText}
                  />
                  <FormInput
                    label="Currency"
                    value={
                      formData.currency && formData.currency?.toUpperCase()
                    }
                    onChange={handleFormChange("currency")}
                    placeholder="Enter Currency"
                    error={errors?.currency?.error}
                    helperText={errors?.currency?.errorText}
                    disabled
                  />
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        backgroundColor: "transparent",
                        padding: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <label
                        style={{
                          marginRight: "8px",
                          color: "#fff",
                          width: "13%",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Category:
                        <span style={{ color: "red", marginLeft: 4 }}>*</span>
                      </label>
                      <FormControl
                        fullWidth
                        size="small"
                        error={errors?.wordpressCategoryId?.error}
                        sx={{
                          width: "87%",
                          borderRadius: "4px",
                        }}
                      >
                        <Select
                          id="wordpress-category-select"
                          multiple
                          displayEmpty
                          open={categoryOpen}
                          onOpen={() => setCategoryOpen(true)}
                          onClose={() => {
                            if (!categorySavedRef.current) {
                              const savedIds = savedCategoryNames
                                .map((name) => {
                                  const cat = categories.find((c) => c.name === name);
                                  return cat ? String(cat.id) : null;
                                })
                                .filter(Boolean);
                              setFormData((prev) => ({
                                ...prev,
                                wordpressCategoryId: savedIds,
                                wordpressCategoryName: savedCategoryNames,
                              }));
                            }
                            categorySavedRef.current = false;
                            setCategoryOpen(false);
                          }}
                          value={formData.wordpressCategoryId}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 320,
                                width: 320,
                              },
                            },
                            MenuListProps: {
                              disablePadding: true,
                            },
                          }}
                          renderValue={() => {
                            if (displayCategoryNames && displayCategoryNames.length > 0) {
                              return <span style={{ color: "#000" }}>{displayCategoryNames.join(", ")}</span>;
                            }
                            return <span style={{ color: "#999" }}>Select Category</span>;
                          }}
                          onChange={(e) => {
                            const selectedIds = Array.isArray(e.target.value)
                              ? e.target.value.map((id) => String(id))
                              : [String(e.target.value)];
                            const selectedNames = categories
                              .filter((item) => selectedIds.includes(String(item.id)))
                              .map((item) => item.name);
                            setFormData((prev) => ({
                              ...prev,
                              wordpressCategoryId: selectedIds,
                              wordpressCategoryName: selectedNames,
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              wordpressCategoryId: validateField(
                                "wordpressCategoryId",
                                selectedIds,
                                {
                                  ...formData,
                                  wordpressCategoryId: selectedIds,
                                }
                              ),
                            }));
                          }}
                          sx={{
                            color: "#000",
                            ".MuiSelect-select": {
                              padding: "8.5px 14px",
                              color: "#000 !important",
                            },
                            "&.Mui-error .MuiSelect-select": {
                              color: "#000 !important",
                            },
                          }}
                        >
                          {categories.map((item) => (
                            <MenuItem
                              key={item.id}
                              value={String(item.id)}
                              dense
                            >
                              <Checkbox
                                checked={formData.wordpressCategoryId.includes(String(item.id))}
                                sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }}
                              />
                              {item.name}
                            </MenuItem>
                          ))}
                          <Box
                            sx={{
                              position: "sticky",
                              bottom: 0,
                              background: "#fff",
                              borderTop: "1px solid #e0e0e0",
                              padding: "8px 12px",
                              display: "flex",
                              justifyContent: "flex-end",
                              zIndex: 1,
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                height: 32,
                                minWidth: 64,
                                fontSize: "0.8rem",
                                textTransform: "none",
                              }}
                              onClick={() => {
                                handleSaveCategories();
                              }}
                            >
                              Save
                            </Button>
                          </Box>
                        </Select>
                        {errors?.wordpressCategoryId?.error && (
                          <FormHelperText error>
                            {errors?.wordpressCategoryId?.errorText}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  </Grid>
                  <FormInput
                    label="Amount"
                    type="number"
                    value={formData.amount}
                    onChange={handleFormChange("amount")}
                    placeholder="Enter Amount"
                    error={errors?.amount?.error}
                    helperText={errors?.amount?.errorText}
                  />
                  <FormInput
                    label="Description"
                    value={formData.description}
                    onChange={handleFormChange("description")}
                    placeholder="Enter Short Description"
                    error={errors?.description?.error}
                    helperText={errors?.description?.errorText}
                  />

                  {/* Image Upload Field */}
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        backgroundColor: "transparent",
                        padding: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <InputLabel
                        style={{
                          color: "#fff",
                          width: "13%",
                          marginRight: "8px",
                        }}
                      >
                        Program Image:
                      </InputLabel>
                      <div>
                        <Button
                          variant="outlined"
                          component="label"
                          sx={{
                            color: "#000",
                            backgroundColor: "#fff",
                            borderRadius: "5px",
                            border: "1px solid #80808047",
                            textTransform: "none",
                            marginBottom: "8px",
                          }}
                        >
                          Upload Image
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleImageChange}
                          />
                        </Button>

                        {errors?.image?.error && (
                          <FormHelperText error>
                            {errors?.image?.errorText}
                          </FormHelperText>
                        )}
                      </div>

                      {imagePreview && (
                        // <Box sx={{ marginTop: "10px" }}>
                        <img
                          src={imagePreview}
                          alt="Program Preview"
                          style={{
                            maxWidth: "200px",
                            maxHeight: "200px",
                            objectFit: "cover",
                            marginLeft: "10px",
                          }}
                        />
                        // </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        backgroundColor: "transparent",
                        padding: "8px",
                        display: "flex",
                        alignItems: "center",
                        "input[type=radio]": {
                          borderColor: "red",
                          "&:checked": {
                            "&:disabled": {
                              "&:before": {
                                content: '""',
                                top: "1px",
                                right: 0,
                                left: "1px",
                                display: "block",
                                position: "relative",
                                backgroundColor: "#007aff",
                                borderRadius: "50%",
                                height: "11px",
                                width: "11px",
                                opacity: "0.7",
                              },
                            },
                          },
                        },
                      }}
                    >
                      <InputLabel
                        style={{
                          color: "#fff",
                          width: "13%",
                          marginRight: "8px",
                        }}
                      >
                        Type:
                      </InputLabel>
                      <RadioGroup
                        row
                        value={formData.programType}
                        onChange={handleFormChange("programType")}
                        sx={{
                          padding: "7px 15px",
                          fontSize: "14px",
                          backgroundColor: "#fff",
                          border: "unset",
                          borderRadius: "10px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="radio"
                            id="program"
                            name="programType"
                            value="program"
                            checked={formData.programType === "program"}
                            onChange={handleFormChange("programType")}
                            disabled={id != "new"}
                          />
                          <label
                            htmlFor="program"
                            style={{ marginLeft: 8, color: "#000" }}
                          >
                            Program
                          </label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="radio"
                            id="subscription"
                            name="programType"
                            value="subscription"
                            checked={formData.programType === "subscription"}
                            onChange={handleFormChange("programType")}
                            style={{ marginLeft: 8 }}
                            disabled={id != "new"}
                          />
                          <label
                            htmlFor="subscription"
                            style={{ marginLeft: 8, color: "#000" }}
                          >
                            Subscription
                          </label>
                        </div>
                      </RadioGroup>
                    </Box>
                  </Grid>
                  <Box display="flex" alignItems="center">
                    <InputLabel
                      style={{
                        color: "#fff",
                        width: "13%",
                        marginRight: "8px",
                      }}
                    >
                      Show on Vitality portal:
                    </InputLabel>
                    <Checkbox
                      checked={formData.showOnVitalityPortal}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          showOnVitalityPortal: e.target.checked,
                        }))
                      }
                      name="showOnVitalityPortal"
                      color="primary"
                    />
                  </Box>

                  <Box display="flex" alignItems="flex-start">
                    <InputLabel
                      style={{
                        color: "#fff",
                        width: "13%",
                        marginRight: "8px",
                      }}
                    >
                      Available to Free Plan:
                    </InputLabel>
                    <Box>
                      <Checkbox
                        checked={isFreeProgram && formData.availableToFreePlan}
                        disabled={!isFreeProgram}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            availableToFreePlan: e.target.checked,
                          }))
                        }
                        name="availableToFreePlan"
                        color="primary"
                        sx={{ pt: 0 }}
                      />
                      <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>
                        {isFreeProgram
                          ? "Non-members can open this course from Courses without joining."
                          : "Set Amount to 0 on a non-corporate Program to enable this."}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      mb: 2,
                      p: 2,
                      border: "1px solid rgb(39, 51, 52)",
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#fff",
                        fontWeight: 600,
                        mb: 1,
                        textTransform: "uppercase",
                        fontSize: "0.85rem",
                      }}
                    >
                      Portal Menu Options
                    </Typography>
                    <Typography
                      sx={{ color: "#bbb", fontSize: "0.8rem", mb: 2 }}
                    >
                      Select which Vitality portal menu items are included with
                      this {formData.programType === "subscription" ? "subscription" : "program"}.
                      {isNewProgram
                        ? " These settings are saved when you save the program."
                        : " Changes apply immediately for members on this plan."}
                    </Typography>
                    {featuresLoading ? (
                      <Typography sx={{ color: "#fff" }}>Loading…</Typography>
                    ) : (
                      membershipFeatures.map((feature) => (
                        <Box
                          key={feature.key}
                          display="flex"
                          alignItems="flex-start"
                          sx={{ mb: 1.5 }}
                        >
                          <Checkbox
                            checked={Boolean(feature.enabled)}
                            onChange={handleMembershipFeatureToggle(
                              feature.key
                            )}
                            color="primary"
                            sx={{ pt: 0 }}
                          />
                          <Box>
                            <Typography
                              sx={{ color: "#fff", fontWeight: 600 }}
                            >
                              {feature.name}
                            </Typography>
                            {feature.description ? (
                              <Typography
                                sx={{ color: "#aaa", fontSize: "0.75rem" }}
                              >
                                {feature.description}
                              </Typography>
                            ) : null}
                          </Box>
                        </Box>
                      ))
                    )}
                    {!isNewProgram && (
                      <Box display="flex" alignItems="center" gap={2} mt={1}>
                        <Button
                          variant="contained"
                          onClick={handleSaveMembershipFeatures}
                          disabled={
                            featuresLoading ||
                            featuresSaving ||
                            membershipFeatures.length === 0
                          }
                          sx={{
                            background: "#FEBD36",
                            color: "#1a1a14",
                            "&:hover": { background: "#E5A82B" },
                          }}
                        >
                          {featuresSaving
                            ? "Saving…"
                            : "Save Menu Options"}
                        </Button>
                        {featuresMessage ? (
                          <Typography
                            sx={{
                              color: featuresMessage
                                .toLowerCase()
                                .includes("unable")
                                ? "#f44336"
                                : "#8bc34a",
                              fontSize: "0.85rem",
                            }}
                          >
                            {featuresMessage}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                    {isNewProgram && featuresMessage ? (
                      <Typography
                        sx={{
                          color: "#f44336",
                          fontSize: "0.85rem",
                          mt: 1,
                        }}
                      >
                        {featuresMessage}
                      </Typography>
                    ) : null}
                  </Box>

                  {formData.programType === "program" && (
                    <>
                      <Box display="flex" alignItems="center">
                        <InputLabel
                          style={{
                            color: "#fff",
                            width: "13%",
                            marginRight: "8px",
                          }}
                        >
                          Corporate Program:
                        </InputLabel>
                        <Checkbox
                          checked={isCorporate}
                          onChange={(e) => setIsCorporate(e.target.checked)}
                          name="isCorporate"
                          color="primary"
                        />
                      </Box>
                      {!isCorporate && (
                        <Grid item sx={12} md={12}>
                          <FormInput
                            label="LMS Url"
                            value={formData.programUrl}
                            onChange={handleFormChange("programUrl")}
                            placeholder="Enter URL of the course to be mapped with this program in LMS"
                            error={errors?.programUrl?.error}
                            helperText={errors?.programUrl?.errorText}
                          />
                        </Grid>
                      )}
                    </>
                  )}
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        backgroundColor: "transparent",
                        padding: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <label
                        style={{
                          marginRight: "25px",
                          color: "#fff",
                          width: "13%",
                        }}
                      >
                        Duration:
                      </label>{" "}
                      <SingleSelect
                        options={[
                          { value: "Daily", label: "Daily" },
                          { value: "Monthly", label: "Monthly" },
                          { value: "6-Monthly", label: "6 Monthly" },
                          { value: "Annually", label: "Annually" },
                          ...(formData.programType !== "subscription"
                            ? [{ value: "Lifetime", label: "Lifetime" }]
                            : [])
                        ]}
                        value={formData.unit}
                        onChange={handleFormChange("unit")}
                        error={errors.unit}
                        disabled={formData.programType === "program"}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        backgroundColor: "transparent",
                        padding: "8px",
                        display: "flex",
                        alignItems: "baseline",
                      }}
                      style={{ border: "unset" }}
                    >
                      <label
                        style={{
                          color: "#fff",
                          width: "13%",
                          marginRight: "8px",
                        }}
                      >
                        Includes:
                      </label>
                      <Box
                        sx={{
                          width: "87%",
                        }}
                        style={{ border: "unset", backgroundColor: "unset" }}
                      >
                        {editorContents?.map((content, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: "relative",
                              mb: 2,
                              backgroundColor: "#fff",
                            }}
                          >
                            {index !== 0 && (
                              <IconButton
                                onClick={() => handleDelete(index)}
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  zIndex: 1,
                                  color: "#000",
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                            <RichTextEditor
                              key={index}
                              index={index}
                              value={content}
                              onChange={handleEditorChange}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Box
                sx={{
                  mb: 1,
                  mr: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                  button: {
                    backgroundColor: "#0bd7ee",
                    color: "#000",
                    borderColor: "#0bd7ee",
                    textTransform: "unset !important",
                    fontSize: "0.8rem",
                    "&:hover, &,focus": {
                      outline: "none",
                      boxShadow: "none",
                      backgroundColor: "#0bd7ee",
                      borderColor: "#0bd7ee",
                    },
                  },
                }}
              >
                <Button variant="outlined" onClick={addEditor} sx={{ mt: 1 }}>
                  Add Section
                </Button>
              </Box>
              <Box
                sx={{
                  px: 2,
                  pb: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Box
                  sx={{
                    mt: 1,
                    button: {
                      "&:nth-of-type(1)": {
                        backgroundColor: "#fff",
                        color: "#000",
                        borderColor: "#fff",
                        textTransform: "unset !important",
                        fontSize: "0.8rem",
                        mr: 2,
                        "&:hover, &,focus": {
                          outline: "none",
                          boxShadow: "none",
                          backgroundColor: "#fff",
                          borderColor: "#fff",
                        },
                      },
                      "&:nth-of-type(2)": {
                        backgroundColor: "#0bd7ee",
                        color: "#000",
                        borderColor: "#0bd7ee",
                        textTransform: "unset !important",
                        fontSize: "0.8rem",
                        "&:hover, &,focus": {
                          outline: "none",
                          boxShadow: "none",
                          backgroundColor: "#0bd7ee",
                          borderColor: "#0bd7ee",
                        },
                      },
                    },
                  }}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancel}
                    sx={{ mt: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    sx={{ mt: 1 }}
                    disabled={props.saveProgramLoading}
                  >
                    {id != "new" ? "Update" : "Create"}
                  </Button>
                </Box>
              </Box>
            </div>
            <Box
              sx={{ padding: 2 }}
              style={{ display: activeTab === 1 ? "block" : "none" }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: fullHtmlContent }}
                style={{ border: "1px solid #ddd", padding: "10px" }}
              />
            </Box>
          </Box>
        </Box>
      </Hoc>
    </Box>
  );
};

AddProgram.propTypes = {};
AddProgram.defaultProps = {};

const actionCreators = {
  showDetail,
  createProgram,
  getProgramDetails,
  updateProgram,
  uploadFile,
  getMembershipFeatureCatalogue,
  getMembershipFeatures,
  updateMembershipFeatures,
};
const AddProgramWrapper = {
  ".menu-item-container": {
    "& p": {
      color: "#fff",
      opacity: 1,
    },
  },
};
const PrgmSubs = {
  boxShadow:
    "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px",
  width: "100%",
  overflowX: "auto",
  backgroundColor: "rgb(15, 28, 30)",
  border: "1px solid rgb(39, 51, 52)",
  color: "rgb(255, 255, 255)",
  margin: "10px 20px",
  ".FieldsWrap": {
    ".MuiGrid-root ": {
      ".MuiBox-root": {
        borderBottom: "1px solid rgb(89 89 89 / 17%)",
        label: {
          fontSize: "0.8rem",
          textTransform: "uppercase",
        },
        ".MuiFormControl-root": {
          m: "0px !important",
          ".MuiInputBase-root": {
            fieldset: {
              display: "none",
            },
          },
          ".MuiFormLabel-root": {
            "&.MuiInputLabel-shrink": {
              color: "#fff",
            },
          },
        },
      },
    },
  },
};

const EditorWrapper = {
  ".MuiTabs-root": {
    ".MuiTabs-flexContainer": {
      "button[aria-selected='true']": {
        color: "#fff",
        opacity: 1,
      },
      button: {
        color: "#fff",
        opacity: 0.7,
      },
    },
    ".MuiTabs-indicator": {
      backgroundColor: "#fff",
    },
  },
  ".quill ": {
    ".ql-toolbar": {
      ".ql-formats": {
        ".ql-font ": {
          ".ql-picker-label": {
            color: "#fff",
          },
        },
        ".ql-picker": {
          ".ql-picker-label": {
            color: "#fff",
          },
          ".ql-picker-options": {
            ".ql-picker-item": {
              color: "#000",
            },
            svg: {
              line: {
                stroke: "#000",
              },
            },
          },
        },
        "&:nth-of-type(1)": {
          button: {
            svg: {
              path: {
                stroke: "unset",
                fill: "#fff",
              },
            },
          },
        },
        "&:nth-of-type(2)": {
          svg: {
            polyline: {
              fill: "#fff",
              stroke: "#fff",
            },
          },
        },
        "&:nth-of-type(4)": {
          svg: {
            polyline: {
              fill: "unset",
              stroke: "#fff",
            },
            line: {
              opacity: 1,
            },
          },
          ".ql-background": {
            svg: {
              g: {
                "polygon, rect, path": {
                  stroke: "#ffffff7d",
                },
              },
              // polyline:{
              //   fill: "unset",
              //   stroke: "#fff",
              // }
            },
          },
        },
        "&:nth-of-type(7)": {
          button: {
            "&:nth-of-type(2)": {
              svg: {
                polyline: {
                  stroke: "#fff",
                },
              },
            },
          },
        },
      },

      svg: {
        path: {
          stroke: "#fff",
        },
        g: {
          path: {
            fill: "#fff",
            stroke: "#fff",
          },
        },
        "polygon, line, rect": {
          stroke: "#fff",
        },
        polyline: {
          fill: "#fff",
        },
      },
    },
    ".ql-container": {
      ".ql-editor ": {
        minHeight: "100px",
      },
    },
  },
};
export default connect(({ user }) => user, actionCreators)(AddProgram);

const generateHtml = (formData, editorContents, imagePreview) => {
  return `
    <div class="program-content">
      <div>
        <button
          class="buy-now-button"
          style="width: 100%; padding: 10px; font-size: 16px; font-weight: bold; background-color: #e29c2e; pointer-events: auto;">
          Join Now For ${formData.amount} ${formData.currency?.toUpperCase()} 
          (${formData.unit && formData.unit !== "Lifetime"
      ? formData.duration <= 1
        ? "Monthly"
        : `${formData?.duration ? formData?.duration : 6} Months`
      : "Lifetime"
    })
        </button>
      </div>
      <div style="padding-top: 20px; overflow-y: auto; max-height: 80vh;">
        <h1 style="font-weight: 600; text-align: center;">${formData.name || "Program Name"
    }</h1>
        <p style="font-size: 14px; color: gray; text-align: center;">${formData.description || "Description goes here..."
    }</p>
        ${imagePreview && `
        <div style="text-align: center; margin: 20px auto;">
          <img src="${imagePreview}" 
               alt="Program Image" 
               style="border-radius: 8px;">
        </div>`}
        <div class="ql-snow">
        <div class="ql-editor">
          ${editorContents && editorContents?.length > 0 &&
    editorContents.map((content) => `<div>${content}</div>`).join("")
    }
              </div>
        </div>
      </div>
    </div>
  `;
};